import { NavigateFunction } from 'react-router-dom';
import { Notification } from '../types';
import projectsService from '../services/projectsService';

const DOCUMENT_ACTION_TYPES = new Set([
  'document_checker_required',
  'document_approval_required',
  'document_approver_required',
  'document_upload_required',
  'document_rejected',
  'document_preview_ready',
  'document_preview_failed',
]);

const DOCUMENT_INFO_TYPES = new Set([
  'document_approved',
  'document_metadata_mismatch',
]);

function isDocumentType(type: string): boolean {
  return (
    DOCUMENT_ACTION_TYPES.has(type) ||
    DOCUMENT_INFO_TYPES.has(type) ||
    (type.startsWith('document_') && type.endsWith('_required'))
  );
}

function buildDocumentRevisionsPath(
  projectId: number,
  documentId: number,
  revisionId?: number,
  openActions = false
): string {
  const base = `/projects/${projectId}/documents/${documentId}/revisions`;
  if (!revisionId) {
    return base;
  }
  const params = new URLSearchParams({ revisionId: String(revisionId) });
  if (openActions) {
    params.set('dialog', 'actions');
  }
  return `${base}?${params.toString()}`;
}

async function resolveDocumentPath(
  notification: Notification,
  openActions: boolean
): Promise<string | null> {
  if (!notification.relatedId) {
    return null;
  }

  try {
    const context = await projectsService.getDocumentContext(notification.relatedId);
    const shouldOpenActions =
      openActions || DOCUMENT_ACTION_TYPES.has(notification.type);

    return buildDocumentRevisionsPath(
      context.projectId,
      context.documentId,
      notification.revisionId,
      shouldOpenActions
    );
  } catch (error) {
    console.error('Failed to resolve document notification path:', error);
    return null;
  }
}

export async function navigateFromNotification(
  notification: Notification,
  navigate: NavigateFunction
): Promise<void> {
  if (notification.actionUrl) {
    navigate(notification.actionUrl);
    return;
  }

  const { type, relatedId } = notification;

  if (isDocumentType(type)) {
    const path = await resolveDocumentPath(notification, DOCUMENT_ACTION_TYPES.has(type));
    if (path) {
      navigate(path);
      return;
    }
  }

  if (type === 'project_assigned' && relatedId) {
    navigate(`/projects/${relatedId}/documents`);
    return;
  }

  if (type === 'transmittal_sent' && relatedId) {
    try {
      const context = await projectsService.getTransmittalContext(relatedId);
      navigate(`/projects/${context.projectId}/documents?tab=transmittals`);
      return;
    } catch (error) {
      console.error('Failed to resolve transmittal notification path:', error);
    }
  }

  if (type.startsWith('time_off_') || type === 'new_time_off_request' || type === 'event_reminder' || type.startsWith('holiday_balance_')) {
    navigate('/time-off');
    return;
  }

  if (type === 'timesheet_reminder' || type === 'timesheet_updated') {
    navigate('/timesheet');
    return;
  }

  if (type === 'opportunity_next_action_due' || type === 'opportunity_next_action_overdue') {
    navigate('/sales/leads');
    return;
  }
}
